<template>
  <div class="add-existing-projects">
    <div class="projects__header" v-if="step === 'projects'">
      <div class="utility">
        <div class="search">
          <label>Search</label>
          <input type="text"
                 v-model="search"
                 @keyup.enter="toggleSearch()"/>
          <button class="button"
                  v-show="!searchApplied"
                  @click="toggleSearch()">Apply
          </button>
          <button class="button button--clear"
                  v-show="searchApplied"
                  @click="toggleSearch()">Clear
          </button>
        </div>
        <div class="filter">
          <select v-model="ownership"
                  @change="resetProjects">
            <option value="1">Owned by anyone</option>
            <option value="2">Owned by me</option>
            <option value="3">Not owned by me</option>
          </select>
        </div>
        <div class="sort">
          <select v-model="sort"
                  @change="resetProjects">
            <option value="1">Last modified</option>
            <option value="2">Alphabetically (A-Z)</option>
            <option value="3">Alphabetically (Z-A)</option>
            <option value="4">Last created</option>
          </select>
        </div>
      </div>
    </div>
    <div class="folders__header" v-if="step === 'folders'">
      <h3 v-if="activeFolderData?.project?.label">Browsing: {{ activeFolderData.project.label }}</h3>
      <div class="breadcrumbs">
        <div class="breadcrumb breadcrumb--back">
          <div class="breadcrumb__inner"
               @click="goBack">
            <SvgBack/>
          </div>
        </div>
        <div class="breadcrumb">
          <div class="breadcrumb__inner">
            <div class="breadcrumb__link"
                 @click="toProjects">
              All projects
            </div>
          </div>
        </div>
        <div class="breadcrumb"
             v-for="(breadcrumb, index) in activeFolderData?.breadcrumbs"
             :key="index">
          <div class="breadcrumb__inner">
            <div @click="goToFolder(breadcrumb.id, false)"
                 class="breadcrumb__link">
              {{ breadcrumb.label }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="projects"
         v-if="step === 'projects'">
      <div class="project project--create" @click="addProject()">
        <div class="project__inner brand-color-hover brand-border-hover brand-svg-hover">
          <div class="project__add">
            <div class="project__meta">
              Create a new project
            </div>
          </div>
          <div class="project__actions">
              <span class="project__action button project__action--load">
                <SvgPlus/>
              </span>
          </div>
        </div>
      </div>
      <div v-if="projects.length > 0">
        <div v-for="(project, index) in projects"
             :key="index"
             class="project"
             @click.stop="goToFolder(project.folder)">
          <div class="project__inner">
            <div class="project__add">
              <div class="project__meta">
                <div class="project__meta__icon"
                     v-if="project.folder_type === 'quote'">
                  <SvgQuote/>
                </div>
                <div class="project__meta__icon"
                     v-else>
                  <SvgFolders/>
                </div>
                {{ project.label }}
                <small>
                  Last saved {{ project.saved }}<span v-if="!project.ownership">,
                    Created by {{ project.creator }}
                  </span>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else
           class="empty">
        No project found.
      </div>
    </div>
    <div class="folders"
         v-if="step === 'folders'">
      <div class="folders__create">
        <div class="folder__create folder__create--add"
             @click="selectFolderCallback(activeFolderData?.project?.id, activeFolderData.id)">
          <div
            class="folder__create__inner brand-background brand-hover-background-darker brand-hover-svg brand-hover-color brand-hover-border">
            <div class="create__title">
              Add to current
              <span v-if="!activeFolderData?.parent">project</span>
              <span v-else>folder</span>
            </div>
            <div class="create__icon">
              <SvgPlus/>
            </div>
          </div>
        </div>
        <div class="folder__create folder__create--folder"
             @click="addFolder('default')"
             v-if="activeFolderData?.bundle === 'default'">
          <div class="folder__create__inner brand-hover-svg brand-hover-color brand-hover-border">
            <div class="create__title">
              New folder
            </div>
            <div class="create__icon">
              <SvgAddFolder/>
            </div>
          </div>
        </div>
        <div class="folder__create folder__create--folder"
             @click="addFolder('quote')"
             v-if="folderTypesAllowed.includes('quote') && activeFolderData?.bundle === 'default'">
          <div class="folder__create__inner brand-hover-svg brand-hover-color brand-hover-border">
            <div class="create__title">
              New quote
            </div>
            <div class="create__icon">
              <SvgAddQuote/>
            </div>
          </div>
        </div>
        <div class="folder__create folder__create--folder"
             @click="addFolder('board')"
             v-if="folderTypesAllowed.includes('board') && activeFolderData?.bundle === 'default'">
          <div class="folder__create__inner brand-hover-svg brand-hover-color brand-hover-border">
            <div class="create__title">
              New mood board
            </div>
            <div class="create__icon">
              <SvgAddBoard/>
            </div>
          </div>
        </div>
      </div>
      <div v-if="foldersFiltered.length > 0">
        <div v-for="(folderSub, index) in foldersFiltered"
             :key="index"
             class="folder"
             :class="{'folder--selected brand-background': folderSub.active}"
             @click="goToFolder(folderSub.id)">
          <div class="folder__inner brand-color-hover brand-border-hover brand-svg-hover">
            <div class="folder__add">
              <div class="folder__title">
                <div class="folder__title__icon">
                  <SvgQuote v-if="folderSub.bundle === 'quote'"/>
                  <SvgBoard v-else-if="folderSub.bundle === 'board'"/>
                  <SvgFolder v-else/>
                </div>
                <span class="folder__title__text">
                {{ folderSub.label }}
                <small v-if="folderSub.bundle === 'board'">Mood board</small>
                <small v-else-if="folderSub.bundle === 'quote'">Quote</small>
                <small v-else>Folder</small>
              </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else
           class="empty">
        No sub-folders.
      </div>
    </div>
    <FolderSaveModal :visible="visibility.addFolder"
                     :folder="activeFolderData"
                     :type="addFolderType"
                     v-show="visibility.addFolder"
                     @saveFolder="createFolder"
                     @close="visibility.addFolder = false"/>
    <ProjectSaveModal
      v-show="visibility.addProject"
      @saveProject="createProject"
      @close="visibility.addProject = false"/>
  </div>
</template>

<script>
import SvgRight from '@/components/svgs/SvgRight.vue'
import SvgHeart from '@/components/svgs/SvgHeart.vue'
import SvgFolder from '@/components/svgs/SvgFolder.vue'
import SvgBack from '@/components/svgs/SvgBack.vue'
import SvgBoard from '@/components/svgs/SvgBoard.vue'
import SvgQuote from '@/components/svgs/SvgQuote.vue'
import SvgAddFolder from '@/components/svgs/SvgAddFolder.vue'
import SvgAddBoard from '@/components/svgs/SvgAddBoard.vue'
import SvgAddQuote from '@/components/svgs/SvgAddQuote.vue'
import SvgEdit from '@/components/svgs/SvgEdit.vue'
import SvgFolders from '@/components/svgs/SvgFolders.vue'
import SvgPlus from '@/components/svgs/SvgPlus.vue'
import FolderSaveModal from '@/components/modals/FolderSaveModal.vue'
import ProjectSaveModal from '@/components/modals/ProjectSaveModal.vue'
import GenericModal from '@/components/modals/GenericModal.vue'

/**
 * Root page choosing folder or project for quote.
 */
export default {
  name: 'AddToExistingProject',
  components: {
    GenericModal,
    SvgBack,
    SvgPlus,
    SvgFolders,
    ProjectSaveModal,
    SvgEdit,
    SvgRight,
    SvgHeart,
    SvgFolder,
    SvgBoard,
    SvgQuote,
    SvgAddFolder,
    SvgAddBoard,
    SvgAddQuote,
    FolderSaveModal,
  },
  props: {
    folderTypesAllowed: {
      type: Array,
      required: false,
      default: ['default', 'quote', 'board']
    },
    entityType: {
      type: String,
      required: true
    },
    entityBundle: {
      type: String,
      required: true
    },
    selectFolderCallback: {
      required: true,
    }
  },
  data () {
    return {
      step: this.$route.query.step || 'projects',
      projects: [],
      folders: [],
      activeFolder: this.$route.query.folder || null,
      activeFolderData: null,
      page: 0,
      paginationNext: true,
      lastFolders: [],
      lastSteps: [],
      projectsLoading: false,
      addFolderType: 'default',
      sort: 1,
      ownership: 1,
      search: '',
      searchApplied: false,
      visibility: {
        addFolder: false,
        addProject: false,
        addQuote: false,
      }
    }
  },
  computed: {
    ...mapStores(useStore),
    foldersFiltered () {
      return this.folders.filter(folder => {
        // noinspection RedundantIfStatementJS
        if (
          this.entityType === 'ofs_project_item' &&
          this.entityBundle !== 'ofs_ofs_catalog_product' &&
          this.entityBundle !== 'ofs_node_typical' &&
          folder.bundle === 'quote'
        ) {
          return false
        }

        // No additions to orders.
        if ((folder.bundle === 'quote' && folder.order)) {
          return false
        }

        return this.folderTypesAllowed.includes(folder.bundle)
      })
    },
  },
  watch: {
    '$route.query.step' (newStep) {
      this.step = newStep;
    },
    '$route.query.folder' (newFolder) {
      this.activeFolder = newFolder;
      if (newFolder) {
        this.loadFolder(newFolder);
      } else {
        this.reset()
        this.loadProjects();
      }
    },
  },
  mounted () {
    if (this.activeFolder) {
      this.loadFolder(this.activeFolder);
    } else {
      this.loadProjects();
    }
    // Add scroll listener.
    window.addEventListener('scroll', this.projectScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.projectScroll)
  },
  methods: {
    /**
     * Reset the form.
     */
    reset () {
      this.step = 'projects'
      this.projects = []
      this.folders = []
      this.activeFolder = null
      this.activeFolderData = null
      this.page = 0
      this.paginationNext = true
      this.lastSteps = []
      this.lastFolders = []
      this.projectsLoading = false
      this.visibility.addFolder = false
      this.sort = 1
      this.ownership = 1
      this.search = ''
    },
    /**
     * Go inside folder.
     *
     * @param folder
     * @param setHistory
     * @returns {Promise<void>}
     */
    async goToFolder (folder, setHistory = true) {
      this.$router.push({ query: { ...this.$route.query, step: 'folders', folder } });
      if (setHistory) {
        this.lastSteps.push(this.step)
        this.lastFolders.push(this.activeFolder)
      }
      this.activeFolder = folder
      await this.loadFolder(folder)
      this.projects = []
      this.step = 'folders'
    },
    /**
     * Go back to parent folder or project.
     */
    goBack () {
      let lastStep = this.lastSteps.pop()
      let lastFolder = this.lastFolders.pop()
      if (lastStep === 'folders') {
        this.goToFolder(lastFolder, false)
      } else {
        this.$router.push({ query: { ...this.$route.query, step: 'projects', folder: null } });
        this.reset()
        this.loadProjects()
      }
    },
    /**
     * View all projects.
     */
    toProjects () {
      this.$router.push({ query: { ...this.$route.query, step: 'projects', folder: null } });
      this.reset()
      this.loadProjects()
    },
    /**
     * Load projects.
     *
     * @returns {Promise<void>}
     */
    async loadProjects (reset = false) {
      if (reset) {
        this.page = 0
        this.projects = []
        this.paginationNext = 0
      }

      const range = 15
      this.store.setLoading()
      let data
      try {
        data = await useFetchDrupal('/projects/project', {
          method: 'GET',
          params: {
            page: this.page,
            range: range,
            editable: true,
            sort: this.sort,
            ownership: this.ownership,
            search: this.search
          }
        })
      } catch (e) {
        this.store.clearLoading()
        this.store.setMessage({
          type: 'error',
          text: 'Error, please try again.'
        })
        return
      }
      // Set default activation.
      data.projects.forEach(project => {
        project.active = false
      })
      await this.store.clearLoading()

      // If presentation mode, move into default project.
      if (this.projects.length === 1 && this.store.settings.presentation.enabled) {
        const rootProject = this.projects[0]
        await this.goToFolder(rootProject.folder, false)
        return
      }

      this.projects = [...this.projects, ...data.projects]
      this.paginationNext = parseInt(data.end) === 0
    },
    /**
     * Search projects
     */
    toggleSearch () {
      if ((!this.searchApplied && this.search !== '') || this.searchApplied) {
        if (this.searchApplied) {
          this.search = ''
        }
        this.searchApplied = !this.searchApplied
        this.page = 0
        this.loadProjects(true)
      }
    },
    /**
     * Reset projects list.
     */
    resetProjects () {
      this.loadProjects(true)
    },
    /**
     * Load a particular folder and it's sub folders.
     *
     * @param folder
     * @returns {Promise<void>}
     */
    async loadFolder (folder) {
      this.store.setLoading()
      let data
      try {
        data = await useFetchDrupal('/projects/folder/' + folder, {
          method: 'GET'
        })
      } catch (e) {
        this.store.clearLoading()
        this.store.setMessage({
          type: 'error',
          text: 'Error, please try again.'
        })
        return
      }

      this.activeFolderData = data

      let folders = []
      let boards = []
      let quotes = []
      let typicals = []
      data.folders.forEach(folderSub => {
        if (folderSub.bundle === 'default') {
          folderSub.type = 'folder'
          folders.push(folderSub)
        }
      })

      data.folders.forEach(folderSub => {
        if (folderSub.bundle === 'board') {
          folderSub.type = 'board'
          boards.push(folderSub)
        }
      })

      data.folders.forEach(folderSub => {
        if (folderSub.bundle === 'quote') {
          folderSub.type = 'quote'
          quotes.push(folderSub)
        }
      })

      data.folders.forEach(folderSub => {
        if (folderSub.bundle === 'typical') {
          folderSub.type = 'typical'
          typicals.push(folderSub)
        }
      })

      folders.sort(function (a, b) {
        return a.weight - b.weight
      })
      boards.sort(function (a, b) {
        return a.weight - b.weight
      })
      quotes.sort(function (a, b) {
        return a.weight - b.weight
      })
      typicals.sort(function (a, b) {
        return a.weight - b.weight
      })

      data.folders = [...folders, ...boards, ...quotes, ...typicals]

      // Set default activation.
      data.folders.forEach(folderSub => {
        folderSub.active = false
      })
      if (this.type === 'folder') {
        if (this.folder.bundle === 'typical') {
          // Filter out mood boards if moving folder.
          data.folders = data.folders.filter(folderSub => {
            return folderSub.bundle === 'quote'
          })
        } else {
          // Filter out mood boards if moving folder.
          data.folders = data.folders.filter(folderSub => {
            return folderSub.bundle === 'default'
          })
        }
      }
      this.folders = data.folders
      this.store.clearLoading()
    },
    /**
     * Scroll listener to load more projects.
     */
    async projectScroll () {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 && !this.projectsLoading && this.paginationNext) {
        this.projectsLoading = true
        this.page++;
        await this.loadProjects()
        this.projectsLoading = false
      }
    },
    /**
     * Check folder type to make sure it is viable.
     *
     * @param folder
     * @returns {boolean}
     */
    checkValidation (folder) {
      let result = false

      if (folder.bundle === 'default') {
        result = true
      }

      return result
    },
    /**
     * Callback from save folder modal.
     */
    createFolder (payload) {
      this.visibility.addFolder = false
      this.goToFolder(payload.folder.id)
    },
    /**
     * Show add folder modal.
     */
    addFolder (type) {
      this.addFolderType = type
      this.visibility.addFolder = true
    },
    /**
     * Callback from save project modal.
     */
    createProject (payload) {
      this.visibility.addProject = false
      this.goToFolder(payload.folder)
    },
    /**
     * Show add project modal.
     */
    addProject () {
      this.visibility.addProject = true
    },
  }
}
</script>

<!--suppress SassScssResolvedByNameOnly -->
<style lang="scss" scoped>

#quoteInitialize {
  padding: 2rem;
}

.breadcrumbs {
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  padding: 0 0.5rem;
  display: flex;
  font-size: 0.8rem;
  margin: 1rem 0;

  .breadcrumb {
    display: flex;
    padding: 1rem 2rem 1rem 0.5rem;
    position: relative;
    cursor: pointer;

    &:after {
      content: '\00BB';
      position: absolute;
      right: 0;
      width: 1rem;
      height: 1rem;
      color: $main-font-color;
      cursor: pointer;
    }

    &.breadcrumb--back {
      border-right: 1px solid #ccc;
      padding-right: 1rem;
      margin-right: 1rem;

      .breadcrumb__inner {
        cursor: pointer;
        height: 1rem;
      }

      :deep(svg) {
        width: 1rem;
        height: 1rem;

        * {
          fill: $main-font-color !important;
        }
      }

      &:after {
        display: none;
      }
    }

  }

  .breadcrumb:last-child:after {
    display: none;
  }
}

.empty {
  text-align: center;
  font-size: 1rem;
  padding: 2rem 0;
  background: #f5f5f5;
  height: calc(100vh - 19rem);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radus: 0.25rem;
}

.projects, .folders {

  .project, .folder {
    padding: 0 0 1rem;

    &.project--create {

      .project__inner {
        border: 2px dashed $main-font-color;
        border-radius: 0.25rem;
        background: none;
        font-size: 0.8rem;
        text-transform: uppercase;

        .project__actions {

          button, a, span {

            :deep(svg) * {
              fill: $main-font-color !important;
              stroke: none !important;
            }
          }
        }
      }

      &:hover {

        .project__inner {
          cursor: pointer;
          border: 2px dashed $primary-color;
          color: $primary-color;

          .project__actions {

            button, a, span {
              background: $primary-color;

              :deep(svg) * {
                fill: #fff !important;
                stroke: none !important;
              }
            }
          }
        }
      }
    }

    .project__inner, .folder__inner {
      padding: 1rem;
      background: #fff;
      border-radius: 0.25rem;
      height: 100%;
      box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
    }

    .project__add, .folder__add {
      flex: 1;
      display: flex;
      align-items: center;
      cursor: pointer;
      color: $main-font-color;

      &:hover {
        color: $primary-color;

        .project__meta, .folder__meta {
          color: $primary-color;
        }
      }

      .project__meta, .folder__meta {
        flex: 1;
        display: flex;
        align-items: center;
        color: $main-font-color;

        small {
          display: inline-block;
          padding-left: 1rem;
          opacity: 0.6;
        }

        .project__meta__icon, .folder__meta__icon {
          margin-right: 0.5rem;

          :deep(svg) {
            margin: 0 auto;
            display: block;
            width: 1.4rem;
            height: 48px;

            * {
              fill: $main-font-color !important;
            }

            .stroke {
              stroke: $main-font-color !important;
              fill: none !important;
            }
          }
        }
      }


      &:hover {
        color: $primary-color;

        .folder__title {

          .folder__title__icon {

            :deep(svg) {

              * {
                stroke: $primary-color !important;
              }
            }
          }
        }
      }

      .folder__title {
        flex: 1;
        display: flex;
        align-items: center;

        .folder__title__icon {
          margin-right: 0.5rem;

          :deep(svg) {
            margin: 0 auto;
            display: block;
            width: 1.4rem;

            * {
              stroke: $main-font-color !important;
            }
          }
        }

        .folder__title__text {

          small {
            display: block;
            text-transform: uppercase;
            font-size: 0.6rem;
          }
        }
      }
    }

    .project__actions {
      display: block;

      button, a, span {
        flex: 1;
        margin: 0;
        background: #fff;
        box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.2);
        padding: 0.5rem;
        line-height: 100%;

        &:hover {
          background: $primary-color;

          :deep(svg) {
            * {
              stroke: #fff !important;
            }
          }
        }

        :deep(svg) {
          width: 1rem;
          height: 1rem;

          * {
            stroke: $main-font-color !important;
          }
        }
      }
    }
  }
}

.utility {
  display: flex;
  align-items: center;

  .search {
    display: flex;
    align-items: center;
    margin: 1rem 0 1rem;
    flex: 3;

    label {
      font-size: 1rem;
      line-height: 100%;
      border: 1px solid #ccc;
      border-right: 0;
      padding: 0.75rem 1rem;
      margin: 0;
      height: 2.5rem;
      border-top-left-radius: 0.25rem;
      border-bottom-left-radius: 0.25rem;
    }

    input {
      font-size: 1rem;
      line-height: 100%;
      padding: 0.75rem 1rem;
      border: 1px solid #ccc;
      border-radius: 0;
      height: 2.5rem;
    }

    button {
      font-size: 0.8rem;
      line-height: 100%;
      padding: 0.75rem 1rem;
      border: 1px solid #ccc;
      border-left: 0;
      height: 2.5rem;
      border-radius: 0 0.25rem 0.25rem 0;
    }
  }

  select {
    font-size: 1rem;
    line-height: 100%;
    height: 2.5rem;
    padding: 0.5rem 1.5rem 0.5rem 1rem;
    border: 1px solid #ccc;
    display: block;
    width: 100%;
    border-radius: 0.25rem;
  }

  .filter {
    flex: 1;
    padding: 0 1rem;
  }

  .sort {
    flex: 1;
  }
}

.folders__create {
  display: flex;
  align-items: center;
  flex: 1;
  margin-bottom: 1rem;

  .folder__create {
    display: flex;
    align-items: center;
    flex: 1;
    cursor: pointer;

    &.folder__create--add {

      .folder__create__inner {
        border: 0;
        color: #fff !important;
      }
    }

    &.folder__create {
      margin-right: 1rem;

      &:last-child {
        margin-right: 0;
      }
    }

    .folder__create__inner {
      border: 2px dashed $main-font-color;
      border-radius: 0.25rem;
      background: none;
      font-size: 0.8rem;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      flex: 1;
      padding: 1rem;

      .create__title {
        flex: 1;
      }

      .create__icon {
        background: #fff;
        padding: 0.3rem;
        box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.2);
        border-radius: 0.25rem;
        display: flex;
        align-items: center;

        :deep(svg) {
          width: 1.4rem;
          height: 1.4rem;
        }
      }
    }
  }
}

.button.brand-hover-background {
  &:hover {
    color: #fff;
  }
}

</style>
